import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  TextField,
  List,
  ListItem,
  ListItemText,
  Paper,
  Box,
  BoxProps,
  InputAdornment,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material/styles';
import { getAutoCompleteAddressInputStyles } from './AutoCompleteAddressInput.styles';
import {
  fetchPlaceAutocomplete,
  fetchPlaceDetails,
  isAllowedCountry,
  type PlaceAddressDetails as AddressDetails,
  type PlaceSuggestion as AddressSuggestion,
} from '@/utils/placesApi';

interface AutoCompleteAddressInputProps extends Omit<BoxProps, 'onChange'> {
  value: string;
  onChange: (address: string) => void;
  onAddressSelect: (addressDetails: AddressDetails) => void;
  placeholder?: string;
  disabled?: boolean;
  // Sets native required/aria-required; visible asterisk is on the caller's label.
  required?: boolean;
  error?: boolean;
  helperText?: string | null;
  name?: string;
  size?: 'small' | 'medium';
  // Optional clear callback. When provided, the field renders a small "x"
  // adornment at the right end whenever ``value`` is non-empty. Clicking
  // the adornment invokes the callback — useful for letting users wipe
  // the field without selecting-all + delete on touch devices. MUI's
  // ``endAdornment`` slot reserves space at the right edge so long
  // addresses scroll under the cursor, never under the icon.
  onClear?: () => void;
  // Accessible name for the input. Defaults to "Street address" if the
  // caller doesn't pass a more specific label (e.g. "Voting address").
  // The visible placeholder is not a reliable label per WCAG 2.5.3, so
  // an explicit aria-label always renders on the input.
  ariaLabel?: string;
  // Position of the suggestions dropdown relative to the input.
  // ``'below'`` (default) drops down beneath the field — the natural
  // choice when there's room. ``'above'`` flips it on top of the
  // field — required when the input sits near the bottom of the
  // viewport where the on-screen keyboard would otherwise obscure a
  // below-dropdown (e.g., the MobileAddressBar that docks above the
  // keyboard).
  dropdownDirection?: 'above' | 'below';
  // Forwarded to the underlying TextField's ``inputRef`` so callers
  // can programmatically focus the actual ``<input>`` element. Used
  // by ``MobileAddressBar`` to let the LeftPaneMenu's "My Ballot
  // Preview" item focus the address input in the no-address state.
  inputRef?: React.Ref<HTMLInputElement>;
}

const AutoCompleteAddressInput: React.FC<AutoCompleteAddressInputProps> = ({
  value,
  onChange,
  onAddressSelect,
  placeholder,
  disabled = false,
  required = false,
  error,
  helperText,
  sx,
  id,
  size,
  onClear,
  ariaLabel,
  dropdownDirection = 'below',
  inputRef,
}) => {
  const { t } = useTranslation('common');
  // Fall back to a localized generic label when the caller doesn't pass a
  // more specific accessible name. WCAG 2.5.3 — the placeholder is not a
  // reliable label, so an explicit aria-label always renders on the input.
  const resolvedAriaLabel = ariaLabel ?? t('streetAddressAriaLabel');
  const resolvedPlaceholder = placeholder ?? t('addressPlaceholder');
  const [suggestions, setSuggestions] = React.useState<AddressSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = React.useState(false);
  const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  // Thin wrapper over the shared Places client that also drives the
  // dropdown's local state. Network/mapping logic lives in utils/placesApi.
  const runAutocomplete = React.useCallback(async (inputText: string) => {
    const mapped = await fetchPlaceAutocomplete(inputText);
    setSuggestions(mapped);
    setShowSuggestions(mapped.length > 0);
  }, []);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    onChange(newValue);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (newValue.length <= 1) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    debounceRef.current = setTimeout(() => runAutocomplete(newValue), 300);
  };

  const handleSuggestionSelect = async (suggestion: AddressSuggestion) => {
    setShowSuggestions(false);
    onChange(suggestion.description);

    const details = await fetchPlaceDetails(suggestion.place_id);
    if (!details) return;

    // Defensive: reject anything the backend filter let through that isn't
    // the United States or a US territory with US ballots.
    if (!isAllowedCountry(details)) {
      console.warn('Rejected non-US address selection');
      return;
    }

    onAddressSelect(details);
  };

  const handleInputFocus = () => {
    if (suggestions.length > 0) setShowSuggestions(true);
  };

  const handleInputBlur = () => {
    setTimeout(() => setShowSuggestions(false), 200);
  };

  const theme = useTheme();
  const styles = getAutoCompleteAddressInputStyles(theme);

  return (
    <Box sx={styles.container}>
      <TextField
        fullWidth
        variant="outlined"
        size={size}
        value={value}
        inputRef={inputRef}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        placeholder={resolvedPlaceholder}
        disabled={disabled}
        required={required}
        sx={{ ...styles.textField, ...sx }}
        error={error}
        helperText={error && helperText ? helperText : undefined}
        slotProps={{
          input: {
            id: id,
            'aria-label': resolvedAriaLabel,
            // Only render the clear button when the caller opted in AND the
            // field has content. Material convention: no "clear nothing"
            // target on empty inputs.
            endAdornment:
              onClear && value.length > 0 ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    aria-label={t('clearAddress')}
                    edge="end"
                    // Suppress the default mousedown focus shift so the input's
                    // blur doesn't fire first and steal focus to nowhere, and
                    // clear on click so the press stays cancellable (WCAG 2.5.2).
                    onMouseDown={(e) => {
                      e.preventDefault();
                    }}
                    onClick={onClear}
                    sx={{ p: 0.25 }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
          },
        }}
      />

      {showSuggestions && suggestions.length > 0 && (
        <Paper
          elevation={3}
          sx={{
            ...styles.suggestionsPaper,
            // Anchor the dropdown to the chosen side of the input.
            // ``below`` (default) keeps the existing top-anchored
            // styling. ``above`` swaps to bottom-anchored so the
            // dropdown grows upward — used by the MobileAddressBar
            // where the keyboard would obscure a below-dropdown.
            ...(dropdownDirection === 'above'
              ? { top: 'auto', bottom: '100%', marginTop: 0, marginBottom: '4px' }
              : {}),
          }}
        >
          <List dense>
            {suggestions.map((s) => (
              <ListItem
                key={s.place_id}
                component="div"
                onClick={() => handleSuggestionSelect(s)}
                sx={styles.suggestionItem}
              >
                <ListItemText
                  primary={s.structured_formatting?.main_text || s.description}
                  secondary={s.structured_formatting?.secondary_text}
                  primaryTypographyProps={styles.primaryText}
                  secondaryTypographyProps={styles.secondaryText}
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      )}
    </Box>
  );
};

export default AutoCompleteAddressInput;
